import { FC, useEffect } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { Header, Layout, NextButton } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { CheckTile, NotificationBox } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { ServiceGemakFlowBoilerReachabilityStepRendering } from '@sitecore/types/ServiceGemakFlowBoilerReachabilityStep';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { FlowContextServiceGemak } from '../../context';

interface FormFields {
  kcStairs: boolean;
  kcBereikbaar: boolean;
  kcAccess: boolean;
}

export const ReachabilityStep: FC = () => {
  const { useFlowSelector, useFlowActorRef } = useFlowHooks<FlowContextServiceGemak>();
  const sendToFlowMachine = useFlowActorRef().send;
  const {
    ServiceGemakFlowBoilerReachabilityStep: { fields: textualData },
  } = usePlaceholderContent<{
    ServiceGemakFlowBoilerReachabilityStep: ServiceGemakFlowBoilerReachabilityStepRendering;
  }>();

  const formSchema = yup.object({
    kcStairs: yup.boolean().oneOf([true]),
    kcBereikbaar: yup.boolean().oneOf([true]),
    kcAccess: yup.boolean().oneOf([true]),
  });

  const kcStairs = useFlowSelector(state => state.context.kcStairs);
  const kcAccess = useFlowSelector(state => state.context.kcAccess);
  const kcBereikbaar = useFlowSelector(state => state.context.kcBereikbaar);

  const {
    handleSubmit,
    register,
    getValues,
    formState: { isSubmitSuccessful, isValid, isSubmitted },
  } = useForm<FormFields>({
    mode: 'onChange',
    defaultValues: { kcStairs, kcAccess, kcBereikbaar },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  useEffect(() => {
    if (isSubmitSuccessful) {
      const values = getValues();
      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values,
      });
    }
  }, [isSubmitSuccessful, getValues, sendToFlowMachine]);

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <Header>
        <Header.Title>{textualData?.content.title.value}</Header.Title>
        {textualData.content.content.value && <RichText html={textualData.content.content.value} />}
      </Header>
      {textualData.choice.genericContentRadio.value.options.map(op => (
        <CheckTile key={op.name} {...register(op.name as 'kcAccess' | 'kcStairs' | 'kcBereikbaar')}>
          {op.label}
        </CheckTile>
      ))}
      {isSubmitted && !isValid && (
        <NotificationBox
          isAlert={false}
          title={textualData.notifications.genericErrorNotification.value.title}
          text={textualData.notifications.genericErrorNotification.value.content}
          variant={textualData.notifications.genericErrorNotification.value.variant}
        />
      )}
      <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText.value}</NextButton>
    </Layout.Content>
  );
};
